import React, { useEffect, useState } from "react";

import { digitsEnToFa, digitsFaToEn } from "@persian-tools/persian-tools";

import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Card from "@mui/material/Card";
import Stack from "@mui/material/Stack";
import Grid2 from "@mui/material/Grid2";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CardContent from "@mui/material/CardContent";
import CardActionArea from "@mui/material/CardActionArea";
import TodayRoundedIcon from "@mui/icons-material/TodayRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import NavigateNextRoundedIcon from "@mui/icons-material/NavigateNextRounded";
import NavigateBeforeRoundedIcon from "@mui/icons-material/NavigateBeforeRounded";

import moment from "moment";
import pholiday from "pholiday";
import jMoment from "moment-jalaali";
import NewDialog from "components/NewDialog";
import NewDialogTitle from "components/NewDialogTitle";
import NewDialogContent from "components/NewDialogContent";
import NewDialogActions from "components/NewDialogActions";
import {
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";

const weekDaysTitle = [
  {
    id: "01",
    label: "شنبه",
  },
  {
    id: "02",
    label: "یکشنبه",
  },
  {
    id: "03",
    label: "دوشنبه",
  },
  {
    id: "04",
    label: "سه شنبه",
  },
  {
    id: "05",
    label: "چهارشنبه",
  },
  {
    id: "06",
    label: "پنجشنبه",
  },
  {
    id: "07",
    label: "جمعه",
  },
];

const months = [
  {
    id: 1,
    label: "فروردین",
  },
  {
    id: 2,
    label: "اردیبهشت",
  },
  {
    id: 3,
    label: "خرداد",
  },
  {
    id: 4,
    label: "تیر",
  },
  {
    id: 5,
    label: "مرداد",
  },
  {
    id: 6,
    label: "شهریور",
  },
  {
    id: 7,
    label: "مهر",
  },
  {
    id: 8,
    label: "آبان",
  },
  {
    id: 9,
    label: "آذر",
  },
  {
    id: 10,
    label: "دی",
  },
  {
    id: 11,
    label: "بهمن",
  },
  {
    id: 12,
    label: "اسفند",
  },
];

function findInArray(arr, position) {
  if (position < 0) return arr[arr.length + position];
  return arr[position % arr.length];
}

function helper(date, format) {
  return Number(digitsFaToEn(jMoment(date).format(format)));
}

export default function Date(props) {
  const {
    schema,
    formData,
    onChange,
    disabled,
    readonly,
    rawErrors,
    required,
    options,
  } = props;

  const { afterDate, beforDate } = schema;

  const closeOnSelect = true;
  const [open, setOpen] = useState(false);

  const init = digitsFaToEn(jMoment().format("YYYY-MM-DD"));
  const [date, setDate] = useState(formData || init);
  const [selectedValue, setSelectedValue] = useState(formData || init);

  const [day, setToday] = useState(helper(date, "jDD"));
  const [year, setYear] = useState(helper(date, "jYYYY"));
  const [month, setMonth] = useState(helper(date, "jMM"));

  const onOpen = () => setOpen(true);
  const onClose = () => setOpen(false);

  const onPrevMonth = () => {
    if (month === 1) {
      setMonth(12);
      return setYear((prev) => prev - 1);
    }
    return setMonth((prev) => prev - 1);
  };
  const onNextMonth = () => {
    if (month === 12) {
      setMonth(1);
      return setYear((prev) => prev + 1);
    }
    return setMonth((prev) => prev + 1);
  };

  const goToday = () => {
    setSelectedValue(init);
    setToday(helper(undefined, "jDD"));
    setMonth(helper(undefined, "jMM"));
    setYear(helper(undefined, "jYYYY"));
    closeOnSelect && onClose();
  };

  const mons = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];
  const daysInMonth = jMoment.jDaysInMonth(year, Number(month - 1));

  const firstDay =
    (moment(
      jMoment(
        `${year}-${findInArray(mons, month - 1)}-01`,
        "jYYYY/jMM/jDD"
      ).format("YYYY-MM-DD")
    ).day() +
      1) %
    7;

  const extend = (daysInMonth + firstDay) % 7;

  const onSelect = (day) => {
    setSelectedValue(
      digitsFaToEn(
        jMoment(
          `${year}-${month}-${day - firstDay + 1}`,
          "jYYYY/jMM/jDD"
        ).format("YYYY-MM-DD")
      )
    );
    onChange(
      digitsFaToEn(
        jMoment(
          `${year}-${month}-${day - firstDay + 1}`,
          "jYYYY/jMM/jDD"
        ).format("YYYY-MM-DD")
      )
    );
    closeOnSelect && onClose();
  };

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let timer = setTimeout(() => {
      if (loading && formData) {
        setDate(formData || init);
        setSelectedValue(formData || init);
        setToday(helper(formData || init, "jDD"));
        setMonth(helper(formData || init, "jMM"));
        setYear(helper(formData || init, "jYYYY"));
      }
      return setLoading(false);
    }, 500);
    return () => {
      clearTimeout(timer);
    };
  }, [formData]);

  return (
    <>
      <Card
        variant="outlined"
        sx={{
          borderColor:
            rawErrors && rawErrors.length > 0
              ? "error"
              : "action.disabledBackground",
        }}
      >
        <CardActionArea
          onClick={onOpen}
          disabled={disabled || readonly}
          focusRipple={rawErrors && rawErrors.length > 0}
        >
          <Stack
            alignContent="center"
            alignItems="center"
            direction="row"
            height={56}
            px={1}
            columnGap={0.5}
          >
            <Typography
              flexGrow={1}
              fontSize={12}
              align="right"
              variant="body1"
              color="textSecondary"
            >
              {schema.title}
            </Typography>
            <Chip
              sx={{ width: 64, fontSize: 10 }}
              size="small"
              disabled={disabled}
              label={jMoment(selectedValue).format("dddd")}
            />
            <Typography variant="subtitle2" sx={{ mx: 1 }}>
              {formData
                ? digitsEnToFa(jMoment(selectedValue).format("jYYYY-jMM-jDD"))
                : "انتخاب"}
            </Typography>
            <CalendarMonthRoundedIcon color="disabled" fontSize="small" />
          </Stack>
        </CardActionArea>
      </Card>
      <NewDialog label="تقویم" open={open} onClose={onClose}>
        <NewDialogTitle
          onClose={onClose}
          title={digitsEnToFa(
            jMoment(selectedValue).format("dddd jDD jMMMM jYYYY")
          )}
        />
        <NewDialogContent isPopup>
          <Stack p={1} flex={1} rowGap={1} bgcolor="background.paper">
            <Stack direction="row" columnGap={1}>
              <FormControl fullWidth>
                <InputLabel id="month">ماه</InputLabel>
                <Select
                  fullWidth
                  label="ماه"
                  value={month}
                  labelId="month"
                  onChange={(e) => setMonth(e.target.value)}
                >
                  {months.map((m) => (
                    <MenuItem key={m.id} value={m.id}>
                      {m.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="year">سال</InputLabel>
                <Select
                  fullWidth
                  label="سال"
                  value={year}
                  labelId="year"
                  onChange={(e) => setYear(e.target.value)}
                >
                  {Array.from({ length: 101 }, (v, i) => 1320 + i).map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <IconButton size="small" onClick={onNextMonth}>
                <NavigateNextRoundedIcon />
              </IconButton>
              <IconButton size="small" onClick={onPrevMonth}>
                <NavigateBeforeRoundedIcon />
              </IconButton>
            </Stack>

            <WeekDays
              year={year}
              month={month}
              extend={extend}
              firstDay={firstDay}
              daysInMonth={daysInMonth}
              selectedValue={selectedValue}
              onSelect={onSelect}
              afterDate={afterDate}
              beforDate={beforDate}
            />
          </Stack>
        </NewDialogContent>
        <NewDialogActions>
          <Stack
            width="100%"
            alignItems="center"
            direction="row-reverse"
            justifyContent="space-between"
          >
            <Button
              size="medium"
              color="primary"
              variant="contained"
              onClick={onClose}
            >
              بستن
            </Button>
            {jMoment(selectedValue).format("jYYYY-jMM-jDD") !==
              jMoment().format("jYYYY-jMM-jDD") && (
              <Button
                endIcon={<TodayRoundedIcon />}
                size="medium"
                color="primary"
                variant="outlined"
                onClick={goToday}
              >
                برو به امروز
              </Button>
            )}
          </Stack>
        </NewDialogActions>
      </NewDialog>
    </>
  );
}

function WeekDays({
  year,
  month,
  extend,
  firstDay,
  daysInMonth,
  selectedValue,
  onSelect,
  afterDate,
  beforDate,
}) {
  return (
    <Grid2 p={1} container spacing={1} columns={14}>
      {weekDaysTitle.map((item) => (
        <Grid2 key={item.id} size={{ xs: 2 }}>
          <Box bgcolor="action.hover" borderRadius={1} px={0.5}>
            <Typography align="center" fontSize={11} noWrap>
              {item.label}
            </Typography>
          </Box>
        </Grid2>
      ))}
      {[...Array(firstDay + daysInMonth + (7 - extend) || 0)].map((item, i) => {
        if (i < firstDay || i >= daysInMonth + firstDay) {
          return <Grid2 key={i} size={{ xs: 2 }} />;
        }
        const d = digitsFaToEn(
          jMoment(
            `${year}-${month}-${i - firstDay + 1}`,
            "jYYYY/jMM/jDD"
          ).format("jYYYY-jMM-jDD")
        );

        const today = moment(jMoment(d, "jYYYY/jMM/jDD").format("YYYY-MM-DD"));

        const isToday = d === jMoment().format("jYYYY-jMM-jDD");
        const isBetween =
          afterDate && beforDate
            ? moment(today).isBetween(beforDate, afterDate)
            : true;
        const isSelected = d === jMoment(selectedValue).format("jYYYY-jMM-jDD");

        const bgcolor = () => {
          if (isToday) {
            return "primary.lighter";
          }
          if ((i + 1) % 7) {
            return "background.paper";
          }
          return "error.lighter";
        };

        return (
          <Grid2 key={i} size={{ xs: 2 }}>
            <Card
              variant={isSelected && "outlined"}
              sx={{
                bgcolor,
                opacity: isBetween ? 1 : 0.25,
                height: 48,
                borderColor: isSelected && "primary.light",
              }}
            >
              <CardActionArea
                sx={{ height: "100%" }}
                onClick={() => onSelect(i)}
                disabled={!isBetween}
              >
                <CardContent
                  sx={{
                    px: { xs: 0, sm: 1, md: 2 },
                    py: { xs: 0, sm: 2, md: 2 },
                  }}
                >
                  <Typography
                    color={
                      pholiday(
                        `${year}-${month}-${i - firstDay + 1}`,
                        "jYYYY-jMM-jDD"
                      )
                        .events()
                        .find((selectedDay) => selectedDay.isHoliday)
                        ? "error.main"
                        : "textSecondary"
                    }
                    variant="subtitle1"
                    align="center"
                  >
                    {digitsEnToFa(
                      jMoment(
                        `${year}-${month}-${i - firstDay + 1}`,
                        "jYYYY/jMM/jDD"
                      ).format("jDD")
                    )}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid2>
        );
      })}
    </Grid2>
  );
}
